import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AvailablePaymentCurrency, capitalize, formatCurrencyFromCents } from "@simplyk/common";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { formatRelativeDate } from "@/common/helpers/date";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormTooltip } from "@/forms-design-system/FormTooltip";
import { FormTypography } from "@/forms-design-system/FormTypography";

interface FormListRowProps {
  fullName: string | null;
  date: Date;
  amountInCents: number;
  currency: AvailablePaymentCurrency;
  anonymousLabel: string;
  trailingIcon?: ReactNode;
  iconPosition?: "before" | "after";
  iconTooltip?: string;
  now?: Date;
  caption?: ReactNode;
}

export const FormListRow: FC<FormListRowProps> = ({
  fullName,
  date,
  amountInCents,
  currency,
  anonymousLabel,
  trailingIcon,
  iconPosition = "after",
  iconTooltip,
  now,
  caption,
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();

  const formattedAmount = formatCurrencyFromCents(amountInCents, {
    isoLocale,
    currency,
  });

  const iconNode =
    trailingIcon && iconTooltip ? (
      <FormTooltip title={iconTooltip}>
        <Box display="inline-flex">{trailingIcon}</Box>
      </FormTooltip>
    ) : (
      trailingIcon
    );

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <Stack flexGrow={1} minWidth={0}>
        <FormTypography
          variant="body2"
          vibe="moderate"
          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          data-sensitive="person-name"
        >
          {fullName ?? anonymousLabel}
        </FormTypography>
        {caption ?? (
          <FormTypography variant="caption" vibe="quiet">
            {capitalize(formatRelativeDate(date, { locale: isoLocale, now, justNowLabel: t("common", "justNow") }))}
          </FormTypography>
        )}
      </Stack>
      <Stack display="inline-flex" direction="row" alignItems="center" gap={1}>
        {iconPosition === "before" && iconNode}
        <FormTypography variant="h6" vibe="brand">
          {formattedAmount}
        </FormTypography>
        {iconPosition === "after" && iconNode}
      </Stack>
    </Stack>
  );
};
